import { useState, useCallback, useEffect, useMemo } from 'react';
import { TourDefinition, TourStepConfig } from '../types';

/** localStorage key — bump the suffix to force re-show after a major tour update */
const TOUR_KEY = 'maos_tour_inbox_hub_email_v1';

/** The tour ID this hook manages. Matches the TourDefinition.id stored in admin settings. */
const TOUR_ID = 'inbox-hub-email';

export type TourStep = TourStepConfig;

/**
 * Built-in default steps for the Inbox Hub — Email channel.
 * These are used when no admin override has been saved yet.
 * The WhatsApp tour is a separate TourDefinition ("inbox-hub-whatsapp").
 */
export const DEFAULT_INBOX_EMAIL_TOUR_STEPS: TourStepConfig[] = [
  {
    id: 'welcome',
    title: 'Welcome to Inbox Hub',
    description:
      'This is your central workspace for all customer communications. This tour covers the Email Hub — how to read, filter, and reply to inbound experience enquiries. There is a separate WhatsApp tour available when you switch to that channel. Press Next to begin.',
    placement: 'center',
    enabled: true,
  },
  {
    id: 'channel-tabs',
    title: 'Step 1 — Two Channels, One Hub',
    description:
      'These two tabs switch between Email Hub and WhatsApp Hub. The number in brackets is the live count of active items in each channel. You are currently in Email Hub. Clicking WhatsApp Hub will take you to the real-time messaging workspace — that tour is available separately.',
    target: 'channel-tabs',
    placement: 'bottom',
    requiredChannel: 'EMAIL',
    enabled: true,
  },
  {
    id: 'email-status-tiles',
    title: 'Step 2 — Live Status Tiles',
    description:
      'Seven live counters keep you oriented at a glance. ALL shows everything in the inbox. OPEN = cases waiting for action. WAITING CLIENT = you have replied and are waiting for the customer. ASSIGNED = claimed by a team member. NO REPLY = enquiries that have never received a reply. WON and LOST = resolved cases. Click any tile to instantly filter the queue below.',
    target: 'email-header',
    placement: 'bottom',
    requiredChannel: 'EMAIL',
    enabled: true,
  },
  {
    id: 'email-search-filters',
    title: 'Step 3 — Search & Advanced Filters',
    description:
      'The search bar finds emails by customer name, subject line, email address, or estimate number — type and results update immediately. The Filters button reveals advanced options: narrow by category (Private Balloon / Shared Balloon / General Inquiry), by a specific mailbox, by date range, or toggle "Unreplied only" to focus purely on emails that have never received a reply.',
    target: 'email-search',
    placement: 'bottom',
    requiredChannel: 'EMAIL',
    enabled: true,
  },
  {
    id: 'email-queue',
    title: 'Step 4 — Email Queue',
    description:
      'Every inbound enquiry appears here as a card, sorted by the time it was received. Use the Per Page selector and Prev / Next pagination controls at the top-right to navigate large volumes. Loading shows a spinner while the list is refreshing.',
    target: 'email-queue',
    placement: 'top',
    requiredChannel: 'EMAIL',
    enabled: true,
  },
  {
    id: 'sla-indicator',
    title: 'Step 5 — SLA Indicator',
    description:
      'This panel shows when the email arrived and how long ago it was received. The coloured dot at the bottom is the SLA (Service Level Agreement) status. Green = on track. Amber = approaching the deadline, act soon. Red = overdue, immediate attention needed. The vertical coloured bar on the far left edge of the card reflects the same status at a glance across the whole list.',
    target: 'email-sla-bar',
    placement: 'right',
    requiredChannel: 'EMAIL',
    enabled: true,
  },
  {
    id: 'card-badges',
    title: 'Step 6 — Status Badges',
    description:
      'These badges tell you exactly where the case stands. The Case State badge (OPEN, WAITING CLIENT, WON, LOST) tracks the lifecycle. The Verification badge shows whether the lead has been validated. The SLA badge shows the timer value (e.g. AMBER · 21m). If a quote is linked, a quote number badge appears here too — click it to jump straight to that quote.',
    target: 'email-card-badges',
    placement: 'top',
    requiredChannel: 'EMAIL',
    enabled: true,
  },
  {
    id: 'reply-tiles',
    title: 'Step 7 — Reply & Conversion Tiles',
    description:
      'This right-hand panel records what has happened on the case. The Reply tile shows the date, time, delivery status, and method of the last reply sent (e.g. via email, WhatsApp). The Converted tile appears when the enquiry has been turned into a booking. The Last Action tile records who last touched the case and when.',
    target: 'email-card-reply',
    placement: 'left',
    requiredChannel: 'EMAIL',
    enabled: true,
  },
  {
    id: 'assignment',
    title: 'Step 8 — Assignment',
    description:
      'The dark "Assign to me" button claims this case for yourself — it will then appear under the ASSIGNED filter tile and show your name. If the button is not visible, the case is already assigned to someone. Inside the full thread view you can reassign a case to a colleague.',
    target: 'email-card-assign',
    placement: 'left',
    requiredChannel: 'EMAIL',
    enabled: true,
  },
  {
    id: 'open-thread',
    title: 'Step 9 — Opening & Replying to a Thread',
    description:
      'Click anywhere on a card to open the full thread. You will see the complete email conversation, an AI-generated summary with suggested next steps, any linked quote, and a full reply composer at the bottom. You can CC/BCC additional addresses, attach files, choose a signature, and lock the thread while drafting to prevent colleagues from sending at the same time.',
    target: 'email-queue-card',
    placement: 'top',
    requiredChannel: 'EMAIL',
    enabled: true,
  },
  {
    id: 'done',
    title: "You're ready!",
    description:
      "That's the complete Email Hub tour — all 9 steps done. You now know how to read, filter, track SLA, read badges, and reply to every inbound enquiry. Switch to the WhatsApp Hub channel and press the ? button there to start the WhatsApp tour. You can replay this tour any time using the ? button in the channel switcher bar.",
    placement: 'center',
    enabled: true,
  },
];

/**
 * Hook that manages the Inbox Hub guided tour for the Email channel.
 *
 * @param allTours  All TourDefinitions fetched from the admin API.
 *   Pass `null` while still loading (tour auto-start is paused).
 *   Pass `[]` when loaded but nothing was configured (uses built-in defaults).
 *   Pass a non-empty array to look up the 'inbox-hub-email' tour definition.
 */
export function useInboxHubTour(allTours: TourDefinition[] | null = null) {
  const [isActive, setIsActive] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);

  // Resolve which steps to show
  const steps = useMemo<TourStepConfig[]>(() => {
    if (!allTours) return DEFAULT_INBOX_EMAIL_TOUR_STEPS; // still loading
    const match = allTours.find(t => t.id === TOUR_ID);
    if (match && match.published !== false && match.steps.length > 0) {
      return match.steps.filter(s => s.enabled !== false);
    }
    return DEFAULT_INBOX_EMAIL_TOUR_STEPS;
  }, [allTours]);

  // Auto-start once the API fetch has resolved (allTours !== null)
  useEffect(() => {
    if (allTours === null) return; // still loading
    try {
      const done = localStorage.getItem(TOUR_KEY);
      if (!done) {
        const t = setTimeout(() => setIsActive(true), 900);
        return () => clearTimeout(t);
      }
    } catch {
      // localStorage not available
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allTours !== null]); // fire once when loading completes

  const next = useCallback(() => {
    setStepIndex(i => Math.min(i + 1, steps.length - 1));
  }, [steps.length]);

  const back = useCallback(() => {
    setStepIndex(i => Math.max(0, i - 1));
  }, []);

  const skip = useCallback(() => {
    try { localStorage.setItem(TOUR_KEY, '1'); } catch {}
    setIsActive(false);
    setStepIndex(0);
  }, []);

  const complete = useCallback(() => {
    try { localStorage.setItem(TOUR_KEY, '1'); } catch {}
    setIsActive(false);
    setStepIndex(0);
  }, []);

  const restart = useCallback(() => {
    try { localStorage.removeItem(TOUR_KEY); } catch {}
    setStepIndex(0);
    setIsActive(true);
  }, []);

  const currentStep = steps[stepIndex] ?? steps[0];
  const isLast = stepIndex === steps.length - 1;

  return {
    isActive,
    stepIndex,
    currentStep,
    isLast,
    totalSteps: steps.length,
    next,
    back,
    skip,
    complete,
    restart,
  };
}
